import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Grid,
  useToast,
  Heading,
  Flex,
  Button,
  VStack,
  Card,
  CardBody,
  Badge,
  Skeleton,
} from "@chakra-ui/react";

import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../hooks/useAuth";
import axiosInstance from "../../../services/axios";

const SalesData = () => {
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1); // Current page
  const [pageSize] = useState(10); // Items per page
  const [totalItems, setTotalItems] = useState(0); // Total items
  const auth = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const fetchSalesData = useCallback(
    async (pageNumber) => {
      if (!auth.user?.user_id) return;
      
      try {
        setLoading(true);
        const response = await axiosInstance.get(
          `/transactions/sales_data/artist/${auth.user.user_id}`,
          {
            params: { page: pageNumber, page_size: pageSize },
          }
        );
        setSalesData(response.data.sales_data);
        setTotalItems(response.data.total_items);
      } catch (error) {
        toast({
          title: "Error fetching sales data",
          description: error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [auth.user?.user_id, pageSize, toast] // dependencies that might change
  );

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchSalesData(page);
    } else {
      navigate("/login");
    }
  }, [auth.isAuthenticated, auth.user?.user_id, navigate, page, fetchSalesData]);

  const totalPages = Math.ceil(totalItems / pageSize);

  const handleNext = () => {
    if (page < totalPages) setPage((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (page > 1) setPage((prev) => prev - 1);
  };

  // Skeleton loader component
  const SalesDataSkeleton = () => (
    <VStack spacing={{ base: 2, md: 4 }} align="stretch">
      {Array(3).fill(0).map((_, index) => (
        <Card 
          key={index}
          bg="secondary.500"
          shadow="sm" 
          borderRadius={{ base: "lg", md: "xl" }} 
          border="1px" 
          borderColor="secondary.700"
          overflow="hidden"
        >
          <CardBody py={{ base: 3, md: 4 }} px={{ base: 3, md: 6 }}>
            <Grid
              templateColumns={{ 
                base: "1fr auto", 
                md: "2fr 1fr 1fr auto" 
              }}
              gap={{ base: 2, md: 4 }}
              alignItems="center"
              w="100%"
            >
              <VStack align="start" spacing={{ base: 2, md: 1 }}>
                <Skeleton height="20px" width="200px" startColor="dark.500" endColor="secondary.700" />
                <Skeleton 
                  height="16px" 
                  width="150px" 
                  display={{ base: "block", md: "none" }}
                  startColor="dark.500" 
                  endColor="secondary.700" 
                />
              </VStack>
              
              <Skeleton 
                height="16px" 
                width="80px"
                display={{ base: "none", md: "block" }}
                startColor="dark.500" 
                endColor="secondary.700" 
              />
              
              <Skeleton 
                height="24px" 
                width="60px"
                borderRadius="full"
                display={{ base: "none", md: "block" }}
                startColor="dark.500" 
                endColor="secondary.700" 
              />
              
              <Skeleton 
                height="16px" 
                width="16px"
                startColor="dark.500" 
                endColor="secondary.700" 
              />
            </Grid>
          </CardBody>
        </Card>
      ))}
    </VStack>
  );

  if (loading) {
    return (
      <Box maxW="7xl" mx="auto" px={{ base: 2, md: 4 }}>
        <Card bg="dark.100" shadow="xl" borderRadius="2xl" overflow="hidden" border="1px" borderColor="secondary.700">
          <CardBody>
            <Flex 
              direction={{ base: "column", md: "row" }} 
              justify="space-between" 
              align={{ base: "start", md: "center" }}
              mb={{ base: 6, md: 8 }}
              gap={{ base: 2, md: 4 }}
            >
              <VStack align="start" spacing={2}>
                <Skeleton height="32px" width="250px" startColor="dark.500" endColor="secondary.700" />
                <Skeleton height="20px" width="200px" startColor="dark.500" endColor="secondary.700" />
              </VStack>
              <Skeleton 
                height="80px" 
                width="150px" 
                borderRadius="xl"
                startColor="dark.500" 
                endColor="secondary.700" 
              />
            </Flex>
            
            {/* Table Header Skeleton */}
            <Skeleton 
              height="60px" 
              borderRadius="xl" 
              mb={{ base: 4, md: 6 }}
              display={{ base: "none", md: "block" }}
              startColor="dark.500" 
              endColor="secondary.700" 
            />

            <SalesDataSkeleton />
          </CardBody>
        </Card>
      </Box>
    );
  }

  return (
    <Box maxW="7xl" mx="auto" >
      <Card bg="dark.100" shadow="xl" borderRadius="2xl" overflow="hidden" border="1px" borderColor="secondary.700">
        <CardBody>
        
                <Flex 
                direction={{ base: "column", md: "row" }} 
                justify="space-between" 
                align={{ base: "start", md: "center" }}
                mb={{ base: 6, md: 8 }}
                gap={{ base: 2, md: 4 }}
                >
                <VStack align="start" spacing={2}>
                  <Heading 
                  as="h2" 
                  size="xl" 
                  color="primary.900"
                  fontWeight="700"
                  letterSpacing="-0.02em"
                  >
                  Sales Dashboard
                  </Heading>
                  <Text color="primary.500" fontSize="lg">
                  Track your music sales and revenue
                  </Text>
                </VStack>
                <Box 
                  bg="secondary.500"
                  px={6}
                  py={4}
                  borderRadius="xl"
                  border="1px"
                  borderColor="brand"
                >
                  <VStack spacing={1} align="center">
                  <Text fontSize="sm" color="primary.500" fontWeight="500">
                    Account Balance
                  </Text>
                  <Text 
                    fontSize="2xl" 
                    fontWeight="bold" 
                    color="dark.900"
                    letterSpacing="-0.02em"
                  >
                    ${(Number(auth.user?.account?.profile?.balance || 0)).toFixed(2)}
                  </Text>
                  </VStack>
                </Box>
                </Flex>
                {/* Table Header for Desktop */}
          <Box 
            bg="secondary.500" 
            borderRadius="xl" 
            p={{ base: 2, md: 4 }} 
            mb={{ base: 4, md: 6 }}
            display={{ base: "none", md: "block" }}
            border="1px"
            borderColor="secondary.700"
          >
            <Grid
              templateColumns="2fr 1fr 1fr 60px"
              gap={{ base: 3, md: 6 }}
              alignItems="center"
            >
              <Text fontWeight="600" color="primary.500" fontSize="sm">
                TRANSACTION ID
              </Text>
              <Text fontWeight="600" color="primary.500" fontSize="sm">
                DATE
              </Text>
              <Text fontWeight="600" color="primary.500" fontSize="sm">
                SHARES SOLD
              </Text>
              <Text></Text>
            </Grid>
          </Box>

          {/* Sales Data Accordion */}
          <VStack spacing={{ base: 2, md: 4 }} align="stretch">
            {salesData.length > 0 ? (
              <Accordion allowToggle variant="unstyled">
                {salesData.map((sale) => (
                  <AccordionItem key={sale.transaction_id} border="none" mb={3}>
                    <Card 
                      bg="secondary.500"
                      shadow="sm" 
                      borderRadius={{ base: "lg", md: "xl" }} 
                      border="1px" 
                      borderColor="secondary.700"
                      overflow="hidden"
                      transition="all 0.3s ease"
                      _hover={{ shadow: "md", borderColor: "brand" }}
                    >
                      <AccordionButton 
                        p={0} 
                        _hover={{ bg: "transparent" }}
                        _focus={{ boxShadow: "none" }}
                      >
                        <CardBody py={{ base: 3, md: 4 }} px={{ base: 3, md: 6 }} w="100%">
                          <Grid
                            templateColumns={{ 
                              base: "1fr auto", 
                              md: "2fr 1fr 1fr auto" 
                            }}
                            gap={{ base: 2, md: 4 }}
                            alignItems="center"
                            w="100%"
                          >
                            <VStack align="start" spacing={{ base: 2, md: 1 }}>
                              <Text 
                                fontSize={{ base: "sm", md: "md" }} 
                                fontWeight="600"
                                color="primary.900"
                                noOfLines={1}
                              >
                                {sale.transaction_id}
                              </Text>
                              <Text 
                                fontSize="sm" 
                                color="primary.500"
                                display={{ base: "block", md: "none" }}
                              >
                                {new Date(sale.date).toLocaleDateString()} • {sale.total_shares_sold || 0} shares
                              </Text>
                            </VStack>
                            
                            <Text 
                              fontSize="md" 
                              color="primary.500"
                              display={{ base: "none", md: "block" }}
                            >
                              {new Date(sale.date).toLocaleDateString()}
                            </Text>
                            
                            <Badge 
                              bg="brand"
                              color="secondary.100"
                              size="lg"
                              px={3}
                              py={1}
                              borderRadius="full"
                              display={{ base: "none", md: "flex" }}
                              alignItems="center"
                            >
                              {sale.total_shares_sold || 0} shares
                            </Badge>
                            
                            <AccordionIcon 
                              color="brand" 
                              fontSize="xl"
                              transition="transform 0.2s"
                            />
                          </Grid>
                        </CardBody>
                      </AccordionButton>
                      
                      <AccordionPanel p={0}>
                        <Box 
                          bg="dark.500" 
                          px={{ base: 3, md: 6 }} 
                          py={{ base: 3, md: 5 }}
                          borderTop="1px"
                          borderColor="secondary.700"
                        >
                          <VStack align="stretch" spacing={{ base: 3, md: 4 }}>
                            <Text 
                              fontWeight="600" 
                              fontSize="md" 
                              color="primary.900"
                              mb={2}
                            >
                              Track Details
                            </Text>
                            
                            {sale.tracks && sale.tracks.length > 0 ? (
                              <VStack spacing={{ base: 2, md: 3 }} align="stretch">
                                {sale.tracks.map((track, trackIndex) => (
                                  <Box 
                                    key={track.track_id || trackIndex}
                                    bg="secondary.500"
                                    p={{ base: 3, md: 4 }}
                                    borderRadius="lg"
                                    border="1px"
                                    borderColor="secondary.700"
                                  >
                                    <Flex 
                                      justify="space-between" 
                                      align="center"
                                      direction={{ base: "column", sm: "row" }}
                                      gap={{ base: 2, md: 3 }}
                                    >
                                      <VStack align="start" spacing={1} flex={1}>
                                        <Text fontSize="md" fontWeight="600" color="primary.900">
                                          {track.track_name}
                                        </Text>
                                        <Text fontSize="sm" color="primary.500">
                                          by {track.artist_name}
                                        </Text>
                                      </VStack>
                                      <Badge 
                                        bg="brand"
                                        color="dark.900"
                                        size="lg"
                                        px={3}
                                        py={1}
                                        borderRadius="full"
                                      >
                                        {track.shares_sold} shares sold
                                      </Badge>
                                    </Flex>
                                  </Box>
                                ))}
                              </VStack>
                            ) : (
                              <Box 
                                bg="secondary.500" 
                                p={{ base: 4, md: 6 }} 
                                borderRadius="lg" 
                                border="1px" 
                                borderColor="secondary.700"
                                textAlign="center"
                              >
                                <Text color="primary.500" fontSize="sm">
                                  No track details available for this transaction
                                </Text>
                              </Box>
                            )}
                          </VStack>
                        </Box>
                      </AccordionPanel>
                    </Card>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <Card bg="secondary.500" borderRadius={{ base: "lg", md: "xl" }} border="1px" borderColor="secondary.700">
                <CardBody py={{ base: 8, md: 12 }} px={{ base: 4, md: 6 }} textAlign="center">
                  <VStack spacing={{ base: 3, md: 4 }}>
                    <Box 
                      w={16} 
                      h={16} 
                      bg="dark.500" 
                      borderRadius="full" 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center"
                    >
                      <Text fontSize="2xl" color="brand">📊</Text>
                    </Box>
                    <VStack spacing={2}>
                      <Text fontSize="lg" fontWeight="600" color="primary.900">
                        No Sales Data Yet
                      </Text>
                      <Text color="primary.500" maxW="md">
                        Your sales transactions will appear here once you start selling music shares.
                      </Text>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            )}
          </VStack>

          {/* Enhanced Pagination Controls */}
          {salesData.length > 0 && totalPages > 1 && (
            <Flex 
              justify="center" 
              align="center" 
              mt={{ base: 6, md: 8 }} 
              gap={{ base: 4, md: 6 }}
              direction={{ base: "column", sm: "row" }}
            >
              <Button
                onClick={handlePrevious}
                isDisabled={page === 1}
                variant="outline"
                bg="secondary.500"
                color="primary.900"
                borderColor="secondary.700"
                size="md"
                borderRadius="full"
                px={6}
                _hover={{ 
                  bg: "dark.500", 
                  borderColor: "brand" 
                }}
                _disabled={{
                  opacity: 0.4,
                  cursor: "not-allowed",
                  _hover: { bg: "secondary.500" }
                }}
              >
                Previous
              </Button>
              
              <Box 
                bg="dark.500" 
                px={4} 
                py={2} 
                borderRadius="full"
                border="1px"
                borderColor="brand"
              >
                <Text 
                  fontSize="sm" 
                  fontWeight="500" 
                  color="primary.900"
                >
                  Page {page} of {totalPages}
                </Text>
              </Box>
              
              <Button
                onClick={handleNext}
                isDisabled={page >= totalPages}
                variant="outline"
                bg="secondary.500"
                color="primary.900"
                borderColor="secondary.700"
                size="md"
                borderRadius="full"
                px={6}
                _hover={{ 
                  bg: "dark.500", 
                  borderColor: "brand" 
                }}
                _disabled={{
                  opacity: 0.4,
                  cursor: "not-allowed",
                  _hover: { bg: "secondary.500" }
                }}
              >
                Next
              </Button>
            </Flex>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

export default SalesData;